import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SERVICE_LIST } from "@/content/services";

const FIELDS = ["name", "slug", "description", "seo_title", "meta_description", "related_service", "image_url", "sort_order"];

export default function CategoriesAdmin() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/categories", { name, sort_order: cats.length }); setName(""); toast.success("Category added"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = Object.fromEntries(FIELDS.map((k) => [k, editing[k] ?? (k === "sort_order" ? 0 : k === "description" ? "" : null)]));
    try { await api.put(`/admin/categories/${editing.id}`, payload); setEditing(null); toast.success("Category saved"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? It will be removed from ${c.brand_count} brand(s).`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Category deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const set = (k, v) => setEditing((s) => ({ ...s, [k]: v }));

  if (editing) return (
    <form onSubmit={save} className="bg-white border border-[var(--sand)] rounded-lg p-6 lg:p-8 space-y-5" data-testid="category-form">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-[var(--ink)]">Edit {editing.name}</h2>
        <div className="flex gap-2">
          <button type="button" onClick={() => setEditing(null)} className="text-sm px-4 py-2 rounded-md border border-[var(--sand)] hover:border-[var(--ink)]" data-testid="category-form-cancel">Cancel</button>
          <button disabled={busy} className="text-sm px-5 py-2 rounded-md bg-[var(--ink)] text-[var(--cream)] hover:bg-[var(--terracotta)] transition-colors disabled:opacity-60 inline-flex items-center gap-2" data-testid="category-form-save">{busy && <Loader2 size={14} className="animate-spin" />} Save category</button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div><label className="admin-label">Name *</label><input required className="admin-input" value={editing.name} onChange={(e) => set("name", e.target.value)} data-testid="category-edit-name" /></div>
        <div><label className="admin-label">URL slug (/range/…)</label><input className="admin-input" value={editing.slug || ""} onChange={(e) => set("slug", e.target.value)} data-testid="category-edit-slug" /></div>
        <div className="md:col-span-2"><label className="admin-label">Category page intro (shown above the ranges, important for SEO)</label><textarea rows={5} className="admin-input" value={editing.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="category-edit-description" /></div>
        <div><label className="admin-label">SEO title <span className="text-[var(--muted)] font-normal">({(editing.seo_title || "").length}/60)</span></label><input className="admin-input" value={editing.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} data-testid="category-edit-seo-title" /></div>
        <div><label className="admin-label">Related service page</label>
          <select className="admin-input" value={editing.related_service || ""} onChange={(e) => set("related_service", e.target.value || null)} data-testid="category-edit-service">
            <option value="">None</option>
            {SERVICE_LIST.map((s) => <option key={s.slug} value={s.slug}>{s.navLabel}</option>)}
          </select>
        </div>
        <div className="md:col-span-2"><label className="admin-label">Meta description <span className="text-[var(--muted)] font-normal">({(editing.meta_description || "").length}/160)</span></label><textarea rows={2} className="admin-input" value={editing.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} data-testid="category-edit-meta" /></div>
        <div><label className="admin-label">Sort order</label><input type="number" className="admin-input" value={editing.sort_order ?? 0} onChange={(e) => set("sort_order", Number(e.target.value))} /></div>
      </div>
    </form>
  );

  return (
    <div data-testid="admin-categories">
      <p className="eyebrow text-[var(--terracotta)]">Categories</p>
      <h1 className="font-display text-4xl mt-2 text-[var(--ink)]">{cats.length} product categories</h1>
      <p className="mt-3 text-sm text-[var(--muted)] max-w-xl">Each category has its own indexable page at /range/&lt;slug&gt;. Edit a category to write its intro copy, SEO title and meta description.</p>
      <form onSubmit={add} className="mt-8 flex gap-2 max-w-md">
        <input required className="admin-input" placeholder="New category name (e.g. Butter)" value={name} onChange={(e) => setName(e.target.value)} data-testid="category-name-input" />
        <button className="btn-pill btn-ink !py-2.5 !px-4 shrink-0" data-testid="category-add-button"><Plus size={14} /> Add</button>
      </form>
      <ul className="mt-8 bg-white border border-[var(--sand)] rounded-lg divide-y divide-[var(--sand)]">
        {cats.map((c) => (
          <li key={c.id} className="flex items-center gap-4 p-4" data-testid={`category-row-${c.slug}`}>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[var(--ink)]">{c.name} <span className="text-xs text-[var(--muted)] font-normal ml-2">{c.brand_count} ranges · /range/{c.slug}</span></p>
              <p className="text-xs text-[var(--muted)] truncate mt-0.5">{c.description ? c.description : <span className="text-[var(--terracotta)]">No intro copy yet</span>}</p>
            </div>
            <a href={`/range/${c.slug}`} target="_blank" rel="noreferrer" className="p-2 text-[var(--charcoal)] hover:text-[var(--ink)]" title="View page"><ExternalLink size={15} /></a>
            <button onClick={() => setEditing({ ...c })} className="p-2 text-[var(--charcoal)] hover:text-[var(--ink)]" data-testid={`category-edit-${c.slug}`}><Pencil size={15} /></button>
            <button onClick={() => remove(c)} className="p-2 text-[var(--charcoal)] hover:text-red-600" data-testid={`category-delete-${c.slug}`}><Trash2 size={15} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
